import { type NewCardComponents } from '@/lib/api';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { Tooltip } from '@/components/Tooltip';
import { ScoreTerm, termTooltip } from '@/components/ScoreTerm';
import { planChipReason } from '@/components/PlanLine';
import { cardImageUris } from '@/lib/utils';
import {
  NEW_CARDS_COPY, NEW_CARDS_SYNERGY_PRESENCE_COPY, RANKING_TERMS,
  SYNERGY_ESTIMATED_MARKER, SYNERGY_HAS_VALUE, synergyReceiptsLine, type TermKey,
} from '@/lib/rankingTerms';

// ─── Chips ───────────────────────────────────────────────────────────────────

/** Score components shown as chips, in display order. */
function chipTerms(c: NewCardComponents): { component: 'role_fit' | 'vu' | 'synergy' | 'plan_fit'; term: TermKey }[] {
  return [
    { component: 'role_fit', term: 'role_need' },
    { component: 'vu', term: c.value_source === 'quality' ? 'role_quality' : 'role_value' },
    { component: 'synergy', term: 'synergy' },
    { component: 'plan_fit', term: 'profile' },
  ];
}

export function TermChips({ c }: { c: NewCardComponents }) {
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-0.5">
      {chipTerms(c).map(({ component, term }) => {
        const isSynergy = term === 'synergy';
        const hasValue = !isSynergy || SYNERGY_HAS_VALUE.has(c.synergy_presence);
        const value = hasValue ? c[component].toFixed(2) : NEW_CARDS_COPY.synergyNa;
        const estimated = isSynergy && c.synergy_presence === 'estimated';
        return (
          <span key={term} data-testid={`term-${term}`} className="flex items-center gap-1">
            {isSynergy ? (
              <Tooltip
                content={
                  <>
                    {termTooltip('synergy')}
                    {c.synergy_presence !== 'measured' && (
                      <div className="mt-1 font-medium">{NEW_CARDS_SYNERGY_PRESENCE_COPY[c.synergy_presence]}</div>
                    )}
                    {estimated && c.synergy_receipts?.length ? (
                      <div className="mt-1">
                        {synergyReceiptsLine(c.synergy_receipts.map(([context_tag, card_tag]) => ({ context_tag, card_tag })))}
                      </div>
                    ) : null}
                  </>
                }
                popupWidth="max-w-[300px]"
              >
                <span tabIndex={0} className="text-xxs px-1 py-[1px] rounded-[3px] bg-[var(--surface-container-high)] text-on-surface-variant cursor-help">
                  {RANKING_TERMS.synergy.label}
                </span>
              </Tooltip>
            ) : (
              <ScoreTerm term={term} extra={term === 'profile' ? planChipReason(c.plan_receipts) : undefined} />
            )}
            <span className={`text-xs tabular-nums ${hasValue ? 'text-on-surface' : 'text-on-surface-variant opacity-60'}`}>{value}</span>
            {estimated && <span className="text-xxs text-on-surface-variant">{SYNERGY_ESTIMATED_MARKER}</span>}
          </span>
        );
      })}
    </span>
  );
}

// ─── Card row ────────────────────────────────────────────────────────────────

export function CardName({ name, className }: { name: string; className: string }) {
  return (
    <span
      className={`decklist-card cursor-help ${className}`}
      onMouseEnter={(e) => showCardTooltip(cardImageUris(name), e)}
      onMouseLeave={hideCardTooltip}
      onFocus={(e) => showCardTooltip(cardImageUris(name), e)}
      onBlur={hideCardTooltip}
      tabIndex={0}
    >
      {name}
    </span>
  );
}
